import { LEVEL_WIDTH, type EnterablePipe, TileData, TileType } from "./types";

export interface EnemySpawn {
  kind: "goomba" | "koopa" | "paratroopa";
  x: number;
}

export interface LevelData {
  tiles: Map<string, TileData>;
  enemies: EnemySpawn[];
  enterablePipes: EnterablePipe[];
  flagX: number;
  castleX: number;
  width: number;
  groundY: number;
}

export function tileKey(x: number, y: number): string {
  return `${x},${y}`;
}

function groundY(): number {
  return 0; // top row of ground is y=0; ground occupies y=0..-1 (we use y up)
}

export function buildLevel(): LevelData {
  const tiles = new Map<string, TileData>();
  const groundTop = -1; // ground occupies y=-1,-2; walking surface is y=0
  const groundDepth = 2;

  const pits: Array<[number, number]> = [
    [69, 70],
    [86, 88],
    [153, 154]
  ];

  const setTile = (x: number, y: number, type: TileType, payload?: TileData["payload"], flower?: boolean): void => {
    tiles.set(tileKey(x, y), { type, payload, broken: false, bump: 0, flower });
  };

  const isPit = (x: number): boolean => pits.some(([a, b]) => x >= a && x <= b);

  // ground: two rows deep
  for (let x = 0; x < LEVEL_WIDTH; x++) {
    if (isPit(x)) continue;
    for (let dy = 0; dy < groundDepth; dy++) {
      setTile(x, groundTop - dy, TileType.Ground);
    }
  }

  // ---- 1-1 features ----
  // early ? (mushroom)
  setTile(16, 4, TileType.Question, { kind: "mushroom" });

  // brick row with coins
  setTile(20, 4, TileType.Brick);
  setTile(21, 4, TileType.Question, { kind: "coin" });
  setTile(22, 4, TileType.Brick);
  setTile(23, 4, TileType.Question, { kind: "coin" });
  setTile(24, 4, TileType.Brick);
  setTile(22, 7, TileType.Question, { kind: "coin" });

  // pipes: (x, height)
  const pipes: Array<[number, number]> = [
    [29, 2],
    [38, 3],
    [46, 4],
    [57, 4],
    [129, 2],
    [134, 2],
    [158, 2],
    [161, 3],
    [164, 4],
    [167, 4]
  ];
  const flowerPipes = new Set([38, 46]); // pipes with flower on top
  for (const [px, h] of pipes) {
    const hasFlower = flowerPipes.has(px);
    for (let dy = 0; dy < h; dy++) {
      setTile(px, groundTop + 1 + dy, TileType.Pipe, undefined, hasFlower);
      setTile(px + 1, groundTop + 1 + dy, TileType.Pipe, undefined, hasFlower);
    }
  }

  // high ? mushroom above the big pipes
  setTile(51, 10, TileType.Question, { kind: "mushroom" });

  // after first pit: block cluster
  setTile(77, 4, TileType.Question, { kind: "coin" });
  setTile(78, 4, TileType.Brick);
  setTile(79, 4, TileType.Brick);
  setTile(80, 4, TileType.Question, { kind: "mushroom" });
  setTile(81, 4, TileType.Brick);
  setTile(82, 4, TileType.Brick);
  for (let x = 80; x <= 83; x++) setTile(x, 7, TileType.Brick);

  // mid bricks
  setTile(91, 4, TileType.Brick);
  setTile(92, 4, TileType.Brick);
  setTile(93, 4, TileType.Brick);

  setTile(101, 4, TileType.Brick);
  setTile(102, 4, TileType.Brick);
  setTile(103, 4, TileType.Brick);
  setTile(106, 4, TileType.Question, { kind: "coin" });

  setTile(109, 4, TileType.Brick);
  setTile(110, 4, TileType.Brick);
  setTile(111, 4, TileType.Brick);
  setTile(113, 4, TileType.Brick);
  setTile(114, 4, TileType.Brick);
  setTile(115, 4, TileType.Brick);
  setTile(116, 4, TileType.Brick);
  setTile(110, 7, TileType.Brick);
  setTile(111, 7, TileType.Brick);
  setTile(112, 7, TileType.Brick);
  setTile(113, 7, TileType.Brick);
  setTile(119, 7, TileType.Question, { kind: "coin" });

  setTile(124, 4, TileType.Brick);
  setTile(125, 4, TileType.Brick);
  setTile(126, 4, TileType.Brick);
  setTile(127, 4, TileType.Brick);
  setTile(128, 4, TileType.Brick);

  setTile(141, 4, TileType.Brick);
  setTile(142, 4, TileType.Brick);
  setTile(143, 4, TileType.Brick);
  setTile(144, 4, TileType.Brick);
  setTile(145, 4, TileType.Brick);
  setTile(146, 4, TileType.Brick);
  setTile(147, 4, TileType.Brick);
  setTile(148, 4, TileType.Brick);

  // final staircase up: 181..184 heights 1..4, flat 185..186 at 4, down 187..190 heights 4..1
  const stair = (x: number, h: number): void => {
    for (let dy = 0; dy < h; dy++) setTile(x, groundTop + 1 + dy, TileType.Block);
  };
  stair(181, 1);
  stair(182, 2);
  stair(183, 3);
  stair(184, 4);
  stair(185, 4);
  stair(186, 4);
  stair(187, 4);
  stair(188, 3);
  stair(189, 2);
  stair(190, 1);

  const flagX = 198;
  for (let y = groundTop + 1; y <= 21; y++) setTile(flagX, y, TileType.Pole);
  setTile(flagX, 22, TileType.Block); // pole top ball

  const castleX = 202;

  const enemies: EnemySpawn[] = [
    { kind: "goomba", x: 22 },
    { kind: "goomba", x: 40 },
    { kind: "goomba", x: 42 },
    { kind: "goomba", x: 51 },
    { kind: "goomba", x: 80 },
    { kind: "goomba", x: 83 },
    { kind: "goomba", x: 86 },
    { kind: "goomba", x: 89 },
    { kind: "goomba", x: 97 },
    { kind: "goomba", x: 100 },
    { kind: "goomba", x: 103 },
    { kind: "goomba", x: 106 },
    { kind: "koopa", x: 107 },
    { kind: "goomba", x: 113 },
    { kind: "goomba", x: 116 },
    { kind: "goomba", x: 124 },
    { kind: "goomba", x: 131 },
    { kind: "goomba", x: 148 },
    { kind: "goomba", x: 150 }
  ];

  // enterable pipes (1-1): x=46 ↔ x=57 (both 4-tall pipes, short secret passage)
  const enterablePipes: EnterablePipe[] = [
    { px: 46, exitX: 57, exitY: 0 },
    { px: 57, exitX: 46, exitY: 0 }
  ];

  return { tiles, enemies, enterablePipes, flagX, castleX, width: LEVEL_WIDTH, groundY: groundY() };
}

const SOLID_TILES = new Set<TileType>([
  TileType.Ground,
  TileType.Brick,
  TileType.Question,
  TileType.QuestionUsed,
  TileType.Pipe,
  TileType.Block
]);

export function isSolidType(t: TileType | undefined): boolean {
  return t !== undefined && SOLID_TILES.has(t);
}
